import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { DataTable } from '@/components/ui/data-table';
import { useExpiry } from '@/hooks/useExpiry';
import { useToast } from '@/hooks/useToast';
import { formatCurrency, cn } from '@/lib/utils';
import {
  AlertTriangle,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Calendar,
  AlertCircle,
  Trash2,
  RotateCcw,
  Eye,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

export default function ExpiryDashboardPage() {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'expiring' | 'expired' | 'quarantine' | 'alerts'>('expiring');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isQuarantineOpen, setIsQuarantineOpen] = useState(false);
  const [quarantineReason, setQuarantineReason] = useState('');
  const [quarantineNotes, setQuarantineNotes] = useState('');

  const { getDashboard, getQuarantineZone, acknowledgeAlert, addToQuarantine, disposeQuarantine, releaseQuarantine } =
    useExpiry();

  const { data: dashboard, isLoading, error, refetch } = getDashboard();
  const { data: quarantineItems } = getQuarantineZone();
  const { data: alerts } = getAlerts();

  const acknowledgeMutation = acknowledgeAlert();
  const quarantineMutation = addToQuarantine();
  const disposeMutation = disposeQuarantine();
  const releaseMutation = releaseQuarantine();

  const handleAcknowledge = async (alertId: string) => {
    try {
      await acknowledgeMutation.mutateAsync(alertId);
      toast.success('Alert acknowledged', 'The alert has been dismissed.');
    } catch {
      toast.error('Error', 'Failed to acknowledge alert.');
    }
  };

  const handleQuarantine = async (item: { item_id: string; location_name: string; batch_number?: string }) => {
    if (!quarantineReason) {
      toast.warning('Reason required', 'Please select a reason for quarantine.');
      return;
    }
    try {
      await quarantineMutation.mutateAsync({
        item_id: item.item_id,
        quantity: 1, // Would need to get from context
        reason: quarantineReason,
        location_id: 'default', // Would need to get from context
        notes: quarantineNotes,
      });
      toast.success('Item quarantined', 'The item has been moved to quarantine.');
      setIsQuarantineOpen(false);
      setQuarantineReason('');
      setQuarantineNotes('');
    } catch {
      toast.error('Error', 'Failed to quarantine item.');
    }
  };

  const handleDispose = async (quarantineId: string) => {
    try {
      await disposeMutation.mutateAsync({ quarantineId, notes: 'Disposed from quarantine' });
      toast.success('Item disposed', 'The item has been disposed.');
    } catch {
      toast.error('Error', 'Failed to dispose item.');
    }
  };

  const handleRelease = async (quarantineId: string) => {
    try {
      await releaseMutation.mutateAsync({ quarantineId, notes: 'Released back to stock' });
      toast.success('Item released', 'The item has been released back to stock.');
    } catch {
      toast.error('Error', 'Failed to release item.');
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return <XCircle className="h-4 w-4" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4" />;
      case 'medium':
        return <Clock className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="p-4 rounded-lg bg-red-50 border-2 border-red-200">
          <div className="flex items-center gap-3">
            <XCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-900 font-semibold">Error loading expiry dashboard</p>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-red-800 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">Expiry Management</h1>
          <p className="text-text-secondary mt-1">Track expiring items and manage FEFO inventory</p>
        </div>
        <Button variant="secondary" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Expired Items</p>
                <p className="text-3xl font-bold text-red-700">{dashboard?.total_expired || 0}</p>
              </div>
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Expiring in 7 Days</p>
                <p className="text-3xl font-bold text-orange-700">{dashboard?.total_expiring_7_days || 0}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">Expiring in 30 Days</p>
                <p className="text-3xl font-bold text-yellow-700">{dashboard?.total_expiring_30_days || 0}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">In Quarantine</p>
                <p className="text-3xl font-bold text-purple-700">{quarantineItems?.length || 0}</p>
              </div>
              <Package className="h-10 w-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button
          variant={activeTab === 'expiring' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('expiring')}
        >
          <Clock className="mr-2 h-4 w-4" />
          Expiring Soon
        </Button>
        <Button
          variant={activeTab === 'expired' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('expired')}
        >
          <XCircle className="mr-2 h-4 w-4" />
          Expired
        </Button>
        <Button
          variant={activeTab === 'quarantine' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('quarantine')}
        >
          <Package className="mr-2 h-4 w-4" />
          Quarantine
        </Button>
        <Button
          variant={activeTab === 'alerts' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('alerts')}
        >
          <AlertTriangle className="mr-2 h-4 w-4" />
          Alerts ({alerts?.length || 0})
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <TableSkeleton rows={10} columns={6} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {activeTab === 'expiring' && 'Items Expiring Soon'}
              {activeTab === 'expired' && 'Expired Items'}
              {activeTab === 'quarantine' && 'Quarantine Zone'}
              {activeTab === 'alerts' && 'Active Alerts'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeTab === 'expiring' && (
              <DataTable
                columns={[
                  {
                    header: 'Item',
                    accessor: (row: (typeof dashboard.expiring_items)[0]) => (
                      <div>
                        <p className="font-medium">{row.item_name}</p>
                        <p className="text-xs text-text-muted">{row.item_code}</p>
                      </div>
                    ),
                  },
                  {
                    header: 'Batch',
                    accessor: (row) => <span className="font-mono text-sm">{row.batch_number}</span>,
                  },
                  {
                    header: 'Location',
                    accessor: (row) => <span>{row.location_name}</span>,
                  },
                  {
                    header: 'Quantity',
                    accessor: (row) => <span className="font-medium">{row.quantity}</span>,
                  },
                  {
                    header: 'Expires',
                    accessor: (row) => (
                      <div>
                        <p className="font-medium">{format(new Date(row.expiry_date), 'MMM d, yyyy')}</p>
                        <p className="text-xs text-text-muted">{row.days_until_expiry} days</p>
                      </div>
                    ),
                  },
                  {
                    header: 'Urgency',
                    accessor: (row) => (
                      <Badge className={getUrgencyColor(row.urgency)}>
                        <span className="flex items-center gap-1">
                          {getUrgencyIcon(row.urgency)}
                          {row.urgency.toUpperCase()}
                        </span>
                      </Badge>
                    ),
                  },
                  {
                    header: 'Actions',
                    accessor: (row) => (
                      <div className="flex gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4 mr-1" />
                              Consume
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Consume via FEFO</DialogTitle>
                              <DialogDescription>
                                First Expired First Out (FEFO) will automatically select the batches with
                                earliest expiry dates.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <p className="text-sm">
                                Consuming <strong>{row.item_name}</strong> - {row.quantity} units
                              </p>
                              <Button className="w-full">Confirm FEFO Consumption</Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedItem(row.item_id);
                            setIsQuarantineOpen(true);
                          }}
                        >
                          <AlertTriangle className="h-4 w-4" />
                        </Button>
                      </div>
                    ),
                  },
                ]}
                data={dashboard?.expiring_items || []}
                emptyMessage="No items expiring soon"
              />
            )}

            {activeTab === 'expired' && (
              <DataTable
                columns={[
                  {
                    header: 'Item',
                    accessor: (row: (typeof dashboard.expired_items)[0]) => (
                      <div>
                        <p className="font-medium">{row.item_name}</p>
                        <p className="text-xs text-text-muted">{row.item_code}</p>
                      </div>
                    ),
                  },
                  {
                    header: 'Batch',
                    accessor: (row) => <span className="font-mono text-sm">{row.batch_number}</span>,
                  },
                  {
                    header: 'Location',
                    accessor: (row) => <span>{row.location_name}</span>,
                  },
                  {
                    header: 'Quantity',
                    accessor: (row) => <span className="font-medium text-red-600">{row.quantity}</span>,
                  },
                  {
                    header: 'Expired On',
                    accessor: (row) => (
                      <span className="text-red-600 font-medium">
                        {format(new Date(row.expiry_date), 'MMM d, yyyy')}
                      </span>
                    ),
                  },
                  {
                    header: 'Actions',
                    accessor: (row) => (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-orange-600"
                          onClick={() => {
                            setSelectedItem(row.item_id);
                            setIsQuarantineOpen(true);
                          }}
                        >
                          <AlertTriangle className="h-4 w-4 mr-1" />
                          Quarantine
                        </Button>
                      </div>
                    ),
                  },
                ]}
                data={dashboard?.expired_items || []}
                emptyMessage="No expired items"
              />
            )}

            {activeTab === 'quarantine' && (
              <DataTable
                columns={[
                  {
                    header: 'Item',
                    accessor: (row: (typeof quarantineItems)[0]) => (
                      <div>
                        <p className="font-medium">{row.item_name}</p>
                        <p className="text-xs text-text-muted">{row.item_code}</p>
                      </div>
                    ),
                  },
                  {
                    header: 'Reason',
                    accessor: (row) => (
                      <Badge variant={row.reason === 'expired' ? 'error' : 'warning'}>{row.reason}</Badge>
                    ),
                  },
                  {
                    header: 'Quantity',
                    accessor: (row) => <span className="font-medium">{row.quantity}</span>,
                  },
                  {
                    header: 'Status',
                    accessor: (row) => (
                      <Badge variant={row.status === 'quarantined' ? 'warning' : 'success'}>{row.status}</Badge>
                    ),
                  },
                  {
                    header: 'Created',
                    accessor: (row) => format(new Date(row.created_at), 'MMM d, yyyy'),
                  },
                  {
                    header: 'Actions',
                    accessor: (row) => (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleRelease(row.quarantine_id)}>
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Release
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDispose(row.quarantine_id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Dispose
                        </Button>
                      </div>
                    ),
                  },
                ]}
                data={quarantineItems || []}
                emptyMessage="No items in quarantine"
              />
            )}

            {activeTab === 'alerts' && (
              <DataTable
                columns={[
                  {
                    header: 'Item',
                    accessor: (row: (typeof alerts)[0]) => (
                      <div>
                        <p className="font-medium">{row.item_name}</p>
                        <p className="text-xs text-text-muted">{row.item_code}</p>
                      </div>
                    ),
                  },
                  {
                    header: 'Type',
                    accessor: (row) => <Badge>{row.alert_type}</Badge>,
                  },
                  {
                    header: 'Urgency',
                    accessor: (row) => (
                      <Badge className={getUrgencyColor(row.urgency)}>
                        {getUrgencyIcon(row.urgency)}
                        {row.urgency.toUpperCase()}
                      </Badge>
                    ),
                  },
                  {
                    header: 'Days Until',
                    accessor: (row) => (
                      <span
                        className={cn(
                          'font-medium',
                          row.days_until_expiry <= 3 ? 'text-red-600' : row.days_until_expiry <= 7 ? 'text-orange-600' : ''
                        )}
                      >
                        {row.days_until_expiry} days
                      </span>
                    ),
                  },
                  {
                    header: 'Actions',
                    accessor: (row) => (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAcknowledge(row.alert_id)}
                        disabled={row.is_acknowledged}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        {row.is_acknowledged ? 'Acknowledged' : 'Acknowledge'}
                      </Button>
                    ),
                  },
                ]}
                data={alerts || []}
                emptyMessage="No active alerts"
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Quarantine Dialog */}
      <Dialog open={isQuarantineOpen} onOpenChange={setIsQuarantineOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move to Quarantine</DialogTitle>
            <DialogDescription>Add item to quarantine zone for inspection or disposal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Reason *</Label>
              <Select value={quarantineReason} onValueChange={setQuarantineReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="recall">Product Recall</SelectItem>
                  <SelectItem value="quality_check">Quality Check Required</SelectItem>
                  <SelectItem value="suspected">Suspected Contamination</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={quarantineNotes}
                onChange={(e) => setQuarantineNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setIsQuarantineOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => handleQuarantine({ item_id: selectedItem || '', location_name: '' })}
                disabled={!quarantineReason}
              >
                Add to Quarantine
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
